import type { DisplayLocation } from "../../types";
import {
  DISPLAY_LOCATIONS,
  DISPLAY_LOCATION_OPTIONS,
} from "../../constants";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import type { WizardTriggerMode } from "./wizardTypes";

export const displayLocationsRequiredRule = {
  validator: (_: unknown, value?: string[]) =>
    value?.length
      ? Promise.resolve()
      : Promise.reject(
          new Error(
            AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_SURFACES_REQUIRED,
          ),
        ),
};

type DisplayLocationDefaultsInput = {
  resultTypeKey?: string;
  eventKey?: string;
  triggerMode?: WizardTriggerMode;
};

const { SPORTSBOOK, CASINO, DISCOVER, PROFILE, DEPOSIT } = DISPLAY_LOCATIONS;

// Keep each mapping in DISPLAY_LOCATION_OPTIONS order.
const RESULT_DISPLAY_LOCATIONS: Record<string, DisplayLocation[]> = {
  show_bonus: [SPORTSBOOK, DISCOVER],
  award_free_cash: [DISCOVER],
  award_free_picks: [SPORTSBOOK, DISCOVER],
  award_free_spins: [CASINO, DISCOVER],
  award_cashback: [DISCOVER],
  create_profit_boost_promotion: [SPORTSBOOK, DISCOVER],
  refund_on_loss: [SPORTSBOOK, DISCOVER],
  award_future_bonus: [SPORTSBOOK, DISCOVER],
  display_banner: [CASINO, DISCOVER],
};

const TRIGGER_DISPLAY_LOCATIONS: Record<string, DisplayLocation[]> = {
  first_time_deposit: [DISCOVER, DEPOSIT],
  makes_purchase: [DISCOVER, DEPOSIT],
  casino_bet_settled: [CASINO, DISCOVER],
  casino_bet_placed: [CASINO, DISCOVER],
  bet_placed: [SPORTSBOOK, DISCOVER],
};

const AFFILIATE_RESULT_DISPLAY_LOCATIONS: Record<string, DisplayLocation[]> = {
  show_bonus: [DISCOVER, PROFILE],
  award_free_cash: [PROFILE],
  award_free_picks: [SPORTSBOOK, PROFILE],
  award_free_spins: [CASINO, PROFILE],
  create_profit_boost_promotion: [SPORTSBOOK, PROFILE],
  refund_on_loss: [SPORTSBOOK, PROFILE],
  award_future_bonus: [SPORTSBOOK, PROFILE],
};

/**
 * Placement defaults for a newly configured bonus step (BONENG-978).
 * Campaign type chooses whether result, trigger, or affiliate-result defaults
 * apply.
 */
export const defaultDisplayLocations = ({
  resultTypeKey,
  eventKey,
  triggerMode = "campaign_start",
}: DisplayLocationDefaultsInput = {}): DisplayLocation[] => {
  if (triggerMode === "referral" || triggerMode === "affiliate_referral") {
    const selected =
      AFFILIATE_RESULT_DISPLAY_LOCATIONS[resultTypeKey ?? ""] ?? [PROFILE];
    return DISPLAY_LOCATION_OPTIONS().map((option) => option.value).filter(
      (location) => selected.includes(location),
    );
  }

  const selected =
    triggerMode === "event"
      ? TRIGGER_DISPLAY_LOCATIONS[eventKey ?? ""]
      : RESULT_DISPLAY_LOCATIONS[resultTypeKey ?? ""];

  return DISPLAY_LOCATION_OPTIONS().map((option) => option.value).filter((location) =>
    (selected ?? [DISCOVER]).includes(location),
  );
};
